import React, { useEffect, useState } from 'react';
import { ActivityIndicator, Modal, View } from 'react-native';
import { translate } from '../../../services/translate';
import FormInput from '../../../components/FormInput';
import FormActions from '../../../components/FormActions';
import { WindowInformation } from '../../../services/window-information';
import { createStyleSheet, UnistylesRuntime, useStyles } from 'react-native-unistyles';
import { accessService } from '../../../services/central-api/access';
import moment from 'moment';
import { ScrollView } from 'react-native-web-hover';
import Toast from 'react-native-toast-message';
import { AlreadyExists } from '../../../services/central-api/base-service';
import { useAuth } from '../../../context/Auth';
import FilterSwitch from '../../../components/Filter/components/FilterSwitch';
import MonthDropdown from '../../../components/MonthDropdown';
import DayOfMonthDropdown from '../../../components/DayOfMonthDropdown';
import YearDropdown from '../../../components/YearDropdown';
import TimePicker from 'react-time-picker';
import MyAppText from '../../../components/MyAppText';

export default function HolidayModal({ onClose, holidayId, isModalVisible }: { onClose: () => void; holidayId?: number; isModalVisible: boolean; }) {
    const { styles, theme } = useStyles(styleSheet);
    const windowInfo = WindowInformation();
    const { user: authenticatedUser } = useAuth();

    const [name, setName] = useState('');
    const [isRecurrent, setIsRecurrent] = useState<boolean>(true);
    const [isWholeDay, setIsWholeDay] = useState<boolean>(true);

    const [day, setDay] = useState<number>(0);
    const [month, setMonth] = useState<number>(0);
    const [year, setYear] = useState<number>(0);
    const [time, setTime] = useState<string>('');
    const [isFetchingData, setIsFetchingData] = useState<boolean>(holidayId && holidayId > 0 ? true : false);

    function isNameInvalid(value: string) {
        return value == '';
    }

    function isFormValid() {
        if (name == '' ||
            day == 0
        ) {
            return false;
        }

        if (!isRecurrent && year == 0) {
            return false;
        }

        if (!isWholeDay && time == '') {
            return false;
        }

        return true;
    }

    function clearValues() {
        setName('');
        setIsRecurrent(true);
        setIsWholeDay(true);
        setDay(0);
        setMonth(0);
        setYear(0);
        setTime('');
    }

    async function getHoliday() {
        try {
            setIsFetchingData(true);
            const holiday = await accessService.getHolidayById(Number(holidayId));
            setName(holiday.name);
            setDay(holiday.day);
            setMonth(holiday.month);

            if (holiday.year) {
                setIsRecurrent(false);
                setYear(holiday.year);
            }

            if (holiday.hourEnd) {
                setIsWholeDay(false);
                setTime(`${holiday.hourEnd}:00`);
            }
        } catch (err) {
            console.error(err);
            Toast.show({
                type: 'sentinelxError',
                text1: translate('unexpectedError'),
            });
            clearValues();
            onClose();
        } finally {
            setIsFetchingData(false);
        }
    }

    async function loadPageInfo() {
        if (!authenticatedUser) {
            return;
        }
        if (holidayId) {
            getHoliday();
        }
    }

    useEffect(() => {
        if (isModalVisible) {
            loadPageInfo();
        }
    }, [holidayId, isModalVisible]);

    async function formSubmit() {
        try {
            const hour = Number(time.split(':')[0] ?? 0);
            if (holidayId) {
                await accessService.updateHoliday({
                    id: holidayId,
                    name: name,
                    day: day,
                    month: month,
                    year: year > 0 ? year : undefined,
                    hourEnd: hour > 0 ? hour : undefined,
                });
            } else {
                await accessService.createHoliday({
                    name: name,
                    day: day,
                    month: month,
                    year: year > 0 ? year : undefined,
                    hourEnd: hour > 0 ? hour : undefined,
                });
            }
            Toast.show({
                type: 'sentinelxSuccess',
                text1: translate('ActionSuccessfully'),
            });
            clearValues();
            onClose();
        } catch (err) {
            if (err instanceof AlreadyExists) {
                return Toast.show({
                    type: 'sentinelxError',
                    text1: translate('holidayAlreadyExist'),
                });
            }
            console.error(err);
            Toast.show({
                type: 'sentinelxError',
                text1: translate('unexpectedError'),
            });
        }
    }

    return (
        <Modal
            transparent={true}
            visible={isModalVisible}
            animationType='fade'
            onRequestClose={() => {
                onClose();
                clearValues();
            }}
        >
            <View style={styles.modalContainer}>
                <View style={styles.centeredView}>
                    <View style={[styles.formContainer, windowInfo.isMobile ? { width: '95%' } : { width: '35%' }]}>
                        {isFetchingData
                            ? <View style={{ flex: 1, alignItems: 'center', justifyContent: 'center', minHeight: 250 }}>
                                <ActivityIndicator size='large' color={theme.colors.loadingColor} />
                            </View>
                            : <>
                                <ScrollView style={{ flex: 1, minWidth: 300, minHeight: 350, }} contentContainerStyle={{ flexDirection: 'column', gap: 12 }}>

                                    <FormInput
                                        label={translate('name')}
                                        placeholder={translate('name')}
                                        value={name}
                                        onChangeText={setName}
                                        invalid={isNameInvalid}
                                    />

                                    <FilterSwitch
                                        value={isRecurrent}
                                        onChange={(val) => {
                                            setIsRecurrent(val);

                                            if (val) {
                                                setYear(0);
                                            } else {
                                                setYear(moment().year());
                                            }
                                        }}
                                        direction='row'
                                        label={translate('recurrent')}
                                    />

                                    {!isRecurrent &&
                                        <YearDropdown
                                            value={year}
                                            setValue={setYear}
                                            zIndex={3}
                                        />
                                    }

                                    <MonthDropdown
                                        value={month}
                                        setValue={setMonth}
                                        zIndex={2}
                                    />

                                    <DayOfMonthDropdown
                                        value={day}
                                        setValue={setDay}
                                        zIndex={1}
                                        month={month}
                                        year={year > 0 ? year : undefined}
                                    />

                                    <FilterSwitch
                                        value={isWholeDay}
                                        onChange={(val) => {
                                            setIsWholeDay(val);

                                            if (val) {
                                                setTime('');
                                            }
                                        }}
                                        direction='row'
                                        label={translate('wholeDay')}
                                    />

                                    {!isWholeDay &&
                                        <View style={{ flex: 1 }}>
                                            <MyAppText
                                                style={styles.label(16)}>
                                                {translate('workStartTime')}
                                            </MyAppText>
                                            <View style={{ flexGrow: 1, maxWidth: 65 }}>
                                                <TimePicker
                                                    disableClock={true}
                                                    format='HH:mm'
                                                    locale='pt-BR'
                                                    maxDetail='minute'
                                                    className={['light', 'lightAlt'].includes(UnistylesRuntime.themeName) ? ['inputColor', `inputColorLight`] : ['inputColor', `inputColorDark`]}
                                                    value={time}
                                                    clearIcon={null}
                                                    onChange={(value) => {
                                                        if (value) {
                                                            setTime(value);
                                                        }
                                                    }}
                                                />
                                            </View>
                                        </View>
                                    }

                                </ScrollView>

                                <FormActions
                                    onSubmit={formSubmit}
                                    onClose={() => {
                                        onClose();
                                        clearValues();
                                    }}
                                    disabled={!isFormValid()}
                                />
                            </>
                        }
                    </View>
                </View>
            </View>
        </Modal>
    );
}

const styleSheet = createStyleSheet((theme) => ({
    formContainer: {
        minWidth: 300,
        minHeight: 150,
        backgroundColor: theme.colors.container.background,
        borderRadius: 4,
        padding: 20,
        rowGap: 25
    },
    modalContainer: {
        flex: 1,
        backgroundColor: '#31313199'
    },
    centeredView: {
        flex: 1,
        justifyContent: 'center',
        alignItems: 'center',
    },
    buttonsContainer: {
        flexDirection: 'row',
        alignItems: 'flex-end',
        gap: 10,
    },
    label: (fontSize) => ({
        color: theme.colors.labelColor,
        fontSize,
    }),
}));
